import { type SubmitExamLocationRequestInfoMutation } from '@exam-center/graphql';
import { Link } from '@exam-center/router';
import {
  Alert,
  AlertTitle,
  Flex,
  KeyboardArrowRightRoundedIcon,
  List,
  ListItem,
  Text,
} from '@shared/components';

const FORM_ROUTES = {
  PHYSICAL_INFO:
    '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId/physical-info',
  COMPUTER_INFO:
    '/:examCenterCode/exam-location/requests/draft/:requestId/room/:roomRequestId/computer-info',
} as const;

interface ErrorDetail {
  computerInfo: string[];
  physicalInfo: string[];
}

interface ErrorDetails {
  [key: string]: ErrorDetail;
}

interface ExamLocationRequestSubmitErrorAlertProps {
  examCenterCode: string;
  requestId: string;
  submitExamLocationRequestResponse: SubmitExamLocationRequestInfoMutation | null;
  roomIndexMap: Record<string, number>;
}

interface RoomErrorInfoProps {
  roomIndex: number;
  examCenterCode: string;
  requestId: string;
  roomRequestId: string;
  detail: ErrorDetail;
}

export default function ExamLocationRequestSubmitErrorAlert({
  examCenterCode,
  requestId,
  submitExamLocationRequestResponse,
  roomIndexMap,
}: ExamLocationRequestSubmitErrorAlertProps) {
  if (!submitExamLocationRequestResponse) {
    return null;
  }

  const { isSuccess, details } = submitExamLocationRequestResponse.ecSubmitExamLocationRequestInfo;

  if (isSuccess || !details || Object.keys(details).length === 0) {
    return null;
  }

  const errorDetails = details as ErrorDetails;

  return (
    <Alert
      severity='error'
      sx={{
        alignItems: 'flex-start',
        '& .MuiAlert-message': {
          overflow: 'unset',
        },
      }}
    >
      <AlertTitle
        variant='body1Semibold'
        sx={(theme) => ({
          m: theme.spacing(0),
          fontWeight: 600,
        })}
      >
        ไม่สามารถส่งคำขออนุมัติได้
      </AlertTitle>
      <Flex sx={{ mt: 1, flexDirection: 'column', gap: 1 }}>
        <Text variant='body2'>กรุณากรอกข้อมูลห้องสอบต่อไปนี้ให้ครบถ้วน:</Text>
        {Object.entries(errorDetails).map(([roomRequestId, detail]) => (
          <RoomErrorInfo
            key={roomRequestId}
            examCenterCode={examCenterCode}
            requestId={requestId}
            roomRequestId={roomRequestId}
            roomIndex={roomIndexMap[roomRequestId]}
            detail={detail}
          />
        ))}
      </Flex>
    </Alert>
  );
}

function ErrorDetailsLink({
  to,
  examCenterCode,
  requestId,
  roomRequestId,
}: {
  to: (typeof FORM_ROUTES)[keyof typeof FORM_ROUTES];
  examCenterCode: string;
  requestId: string;
  roomRequestId: string;
}) {
  return (
    <Link
      to={`${to}?validate=true` as typeof to}
      params={{ examCenterCode, requestId, roomRequestId }}
    >
      <Flex sx={{ display: 'inline-flex', alignItems: 'center', ml: 2 }}>
        <Text variant='body2' sx={{ textDecoration: 'underline', color: 'error.darker' }}>
          ดูรายละเอียด
        </Text>
        <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, color: 'error.darker' }} />
      </Flex>
    </Link>
  );
}

function RoomErrorInfo({
  roomIndex,
  examCenterCode,
  requestId,
  roomRequestId,
  detail,
}: RoomErrorInfoProps) {
  const hasPhysicalInfoError = detail?.physicalInfo?.length > 0;
  const hasComputerInfoError = detail?.computerInfo?.length > 0;

  return (
    <Flex sx={{ flexDirection: 'column', gap: 1 }}>
      <Text variant='body2Semibold'>ห้องสอบที่ {roomIndex + 1}</Text>
      <List
        sx={{
          p: 0,
          pl: 6,
          listStyleType: 'disc',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {hasPhysicalInfoError ? (
          <ListItem sx={(theme) => ({ display: 'list-item', p: 0, ...theme.typography.body2 })}>
            กรุณากรอกข้อมูลลักษณะทางกายภาพ{' '}
            <ErrorDetailsLink
              to={FORM_ROUTES.PHYSICAL_INFO}
              examCenterCode={examCenterCode}
              requestId={requestId}
              roomRequestId={roomRequestId}
            />
          </ListItem>
        ) : null}

        {hasComputerInfoError ? (
          <ListItem sx={(theme) => ({ display: 'list-item', p: 0, ...theme.typography.body2 })}>
            กรุณากรอกข้อมูลเกี่ยวกับอุปกรณ์คอมพิวเตอร์
            <ErrorDetailsLink
              to={FORM_ROUTES.COMPUTER_INFO}
              examCenterCode={examCenterCode}
              requestId={requestId}
              roomRequestId={roomRequestId}
            />
          </ListItem>
        ) : null}
      </List>
    </Flex>
  );
}
